import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import styles from "./Previewer.module.css";
import { ToastContainer } from "react-toastify";
import { pdfjs } from "react-pdf";
import { DocumentCallback } from "react-pdf/dist/shared/types.js";
import { AppState } from "@/redux/types/AppState";
import { useSelector } from "react-redux";
import MemoizedPDFPreview from "../doc/MemoizedPDFPreview";
import { PDFPreviewZoomHandle } from "@/model/props/proj/pdf/PDFPreviewZoomHandle";
import { CompileStatus } from "@/model/proj/compile/CompileStatus";
import { CompileQueue } from "@/model/proj/CompileQueue";
import {
  getLatestCompile,
  getSrcPosition,
  setCompileQueue,
  setLatestCompile,
  showPreviewTab,
  updatePdfUrl,
} from "@/service/project/ProjectService";
import { CompileResultType } from "@/model/proj/compile/CompileResultType";
import { BaseMethods, ResponseHandler } from "rdjs-wheel";
import { ProjInfo } from "@/model/proj/ProjInfo";
import {
  enterFullScreen,
  isProgrammaticScroll,
  restorePdfOffset,
  scrollToPage,
} from "../doc/PDFPreviewHandle";
import { useTranslation } from "react-i18next";
import { buildPdfHttpHeaders, getPdfjsOptions } from "@/config/pdf/PdfJsConfig";
import {
  compile,
  debugApp,
  handleDownloadPdf,
  handleOpenInBrowserDirect,
  handleSrcLocate,
} from "./PreviewerHandler";
import { ListImperativeAPI } from "react-window";
import { PdfOutlineNavRequest } from "@/model/proj/pdf/PdfOutlineNavRequest";
import {
  setContextCompileResultType,
  setAndDispatchPdfPage,
  setAndDispatchActiveOutline,
  setAndDispatchPdfOutline,
} from "@/service/project/preview/PreviewService";
import { usePreviewHandler } from "./usePreviewHandler";
import {
  buildOutlineIndex,
  findActiveOutlineKey,
  OutlineIndexEntry,
  OutlineItemRaw,
  resolveOutlineDestPosition,
  resolveOutlinePageNumber,
} from "../feat/outline/outlineNavigation";
import { getPreviewUrl } from "@/service/file/FileService";
import Split from "@uiw/react-split";
import PdfOutlinePanel from "../feat/outline/PdfOutlinePanel";
import {
  appendCompileLogChunk,
  COMPILE_CLEAR_MARKER,
  CompileLogEntry,
  CompileLogEntryType,
  compileResultFromBackend,
  detectCompileResultFromLog,
  formatCompileLogHtml,
  parseCompileLog,
} from "./compileLogResultDetector";
import { sseLogMessagesToPlainText } from "@/model/proj/compile/CompileLogMarkers";
pdfjs.GlobalWorkerOptions.workerSrc = `/pdfjs-dist/${pdfjs.version}/pdf.worker.min.mjs`;

const extractPdfVersion = (path: string): string => {
  const match = path.match(/[?&]v=([^&]+)/);
  return match ? match[1] : "";
};

export type PreviwerProps = {
  projectId: string;
  viewModel: string;
  curPage?: number;
};

/**
 * Isolates streaming log state from Previewer so SSE log updates do not
 * re-render the PDF tab, header, or page navigation.
 */
const PreviewerLogPanel: React.FC = () => {
  const { t } = useTranslation();
  const streamLogText = useSelector(
    (state: AppState) => state.proj.streamLogText,
  );
  const logText = useSelector((state: AppState) => state.proj.logText);
  const compileStatus = useSelector((state: AppState) => state.proj.compileStatus);
  const compileResultType = useSelector(
    (state: AppState) => state.preview.compileResultType,
  );
  const compileResultTypeRef = React.useRef(compileResultType);
  compileResultTypeRef.current = compileResultType;
  const accumulatedPlainLogRef = React.useRef("");
  const [curLogText, setCurLogText] = useState<string>("");
  const [plainLog, setPlainLog] = useState<string>("");
  const [compStatus, setCompStatus] = useState<CompileStatus>(
    CompileStatus.COMPLETE,
  );
  const [logFilter, setLogFilter] = useState<
    "error" | "warning" | "all" | "raw"
  >("all");
  const [rawLogExpanded, setRawLogExpanded] = useState<boolean>(false);

  const applyDetectedResult = (detected: CompileResultType | null) => {
    if (detected === null) {
      return;
    }
    // 后端 queue.comp_result 是权威结果，日志检测出的"成功"不能覆盖已判定的失败
    if (
      detected === CompileResultType.SUCCESS &&
      compileResultTypeRef.current === CompileResultType.FAILED
    ) {
      return;
    }
    setContextCompileResultType(detected);
  };

  React.useEffect(() => {
    setCompStatus(compileStatus as CompileStatus);
  }, [compileStatus]);

  React.useEffect(() => {
    if (logText && logText === COMPILE_CLEAR_MARKER) {
      accumulatedPlainLogRef.current = "";
      setPlainLog("");
      setCurLogText("");
      return;
    }
    if (logText && logText.length > 0 && logText !== COMPILE_CLEAR_MARKER) {
      setCompStatus(CompileStatus.COMPLETE);
      accumulatedPlainLogRef.current = logText;
      setPlainLog(logText);
      applyDetectedResult(
        detectCompileResultFromLog(logText, compileResultTypeRef.current),
      );
      setCurLogText(formatCompileLogHtml(logText));
    }
  }, [logText]);

  React.useEffect(() => {
    if (streamLogText && streamLogText.length > 0) {
      const chunk = sseLogMessagesToPlainText(streamLogText);
      if (chunk.includes(COMPILE_CLEAR_MARKER)) {
        accumulatedPlainLogRef.current = "";
        setPlainLog("");
        setCurLogText("");
        return;
      }
      setCompStatus(CompileStatus.COMPILING);
      const { plainLog, htmlLog, resultType } = appendCompileLogChunk(
        accumulatedPlainLogRef.current,
        chunk,
        compileResultTypeRef.current,
      );
      accumulatedPlainLogRef.current = plainLog;
      setPlainLog(plainLog);
      applyDetectedResult(resultType);
      setCurLogText(htmlLog);
    }
  }, [streamLogText]);

  const createMarkup = () => {
    return { __html: curLogText };
  };

  const entries = useMemo<CompileLogEntry[]>(
    () => parseCompileLog(plainLog),
    [plainLog],
  );

  const errorEntries = useMemo(
    () => entries.filter((entry) => entry.type === "error"),
    [entries],
  );

  const warningEntries = useMemo(
    () =>
      entries.filter(
        (entry) => entry.type === "warning" || entry.type === "badbox",
      ),
    [entries],
  );

  const shownEntries =
    logFilter === "error"
      ? errorEntries
      : logFilter === "warning"
        ? warningEntries
        : entries;

  const entryBadgeKey: Record<CompileLogEntryType, string> = {
    error: "log_entry_error",
    warning: "log_entry_warning",
    badbox: "log_entry_badbox",
  };

  if (compStatus === CompileStatus.WAITING) {
    return (
      <div className={styles.logLoadingContainer}>
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t("tips_loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  const filterTabs: { key: typeof logFilter; label: string; count?: number }[] =
    [
      { key: "error", label: t("log_filter_error"), count: errorEntries.length },
      {
        key: "warning",
        label: t("log_filter_warning"),
        count: warningEntries.length,
      },
      { key: "all", label: t("log_filter_all"), count: entries.length },
      { key: "raw", label: t("log_filter_raw") },
    ];

  return (
    <div className={styles.logContainer}>
      <div className={styles.logFilterBar}>
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.logFilterBtn} ${
              logFilter === tab.key ? styles.logFilterBtnActive : ""
            }`}
            onClick={() => setLogFilter(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={styles.logFilterCount}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>
      {logFilter === "raw" ? (
        <div className={styles.logRawPanel}>
          <button
            type="button"
            className={styles.rawLogToggle}
            onClick={() => setRawLogExpanded((expanded) => !expanded)}
          >
            {rawLogExpanded ? t("log_collapse_raw") : t("log_expand_raw")}
          </button>
          {rawLogExpanded && (
            <div
              className={styles.logContent}
              id="logtext"
              dangerouslySetInnerHTML={createMarkup()}
            ></div>
          )}
        </div>
      ) : shownEntries.length > 0 ? (
        <div className={styles.logEntryList}>
          {shownEntries.map((entry, index) => (
            <div
              key={`${entry.type}-${index}`}
              className={`${styles.logEntry} ${
                styles[
                  `logEntry${entry.type[0].toUpperCase()}${entry.type.slice(1)}`
                ]
              }`}
            >
              <div className={styles.logEntryHeader}>
                <span className={styles.logEntryBadge}>
                  {t(entryBadgeKey[entry.type])}
                </span>
                {entry.file && (
                  <span className={styles.logEntryMeta}>
                    {entry.file}
                    {entry.line !== undefined ? `:${entry.line}` : ""}
                  </span>
                )}
              </div>
              <pre className={styles.logEntryText}>{entry.text}</pre>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.logEmpty}>{t("log_empty")}</div>
      )}
    </div>
  );
};

const Previewer: React.FC<PreviwerProps> = (props: PreviwerProps) => {
  const [curPdfUrl, setCurPdfUrl] = useState<string>("");
  const [curPreviewTab, setCurPreviewTab] = useState<string>("pdfview");
  const [curProjInfo, setCurProjInfo] = useState<ProjInfo>();
  const [texCompileResult, setTexCompileResult] = useState<CompileResultType>(
    CompileResultType.SUCCESS,
  );
  const [numPages, setNumPages] = useState<number>();
  const [devModel, setDevModel] = useState<boolean>();
  const [pdfProxy, setPdfProxy] = useState<DocumentCallback | null>(null);
  const [outlineIndex, setOutlineIndex] = useState<OutlineIndexEntry[]>([]);
  const [debouncedCurPage, setDebouncedCurPage] = useState<number>(0);
  const [outlineSyncPaused, setOutlineSyncPaused] = useState(false);
  const outlineSyncPausedRef = React.useRef(false);
  const outlineNavHandledIdRef = React.useRef<number>(0);
  const outlineLockRef = React.useRef<{
    key: string;
    ancestorKeys: string[];
    page: number;
    createdAt: number;
    calibrated: boolean;
  } | null>(null);
  const outlineSyncPauseTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const virtualListRef = React.useRef<ListImperativeAPI>(null);
  const pdfPreviewRef = React.useRef<PDFPreviewZoomHandle>(null);
  const { handleScrollTop, handleZoomIn, handleFullScreen, handleZoomOut } =
    usePreviewHandler(
      props.projectId,
      props.viewModel,
      virtualListRef,
      pdfPreviewRef,
    );
  const compileResultType = useSelector(
    (state: AppState) => state.preview.compileResultType,
  );
  const curPage = useSelector((state: AppState) => state.preview.curPage);
  const pdfOutline = useSelector((state: AppState) => state.preview.pdfOutline);
  const outlineNavRequest = useSelector(
    (state: AppState) => state.preview.outlineNavRequest,
  );
  const texPdfUrl = useSelector((state: AppState) => state.proj.texPdfUrl);
  const tabName = useSelector((state: AppState) => state.proj.tabName);
  const texQueue = useSelector((state: AppState) => state.proj.texQueue);
  const latestComp = useSelector((state: AppState) => state.proj.latestComp);
  const projInfo = useSelector((state: AppState) => state.proj.projInfo);
  const endSignal = useSelector((state: AppState) => state.proj.endSignal);
  const remoteCompileResult = useSelector(
    (state: AppState) => state.proj.remoteCompileResult,
  );
  const { t } = useTranslation();

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCurPage(curPage ?? 0);
    }, 150);
    return () => clearTimeout(timer);
  }, [curPage]);

  React.useEffect(() => {
    outlineLockRef.current = null;
    let cancelled = false;
    if (!pdfProxy || !pdfOutline.length) {
      setOutlineIndex([]);
      return;
    }
    buildOutlineIndex(pdfProxy, pdfOutline).then((entries) => {
      if (!cancelled) {
        setOutlineIndex(entries);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [pdfProxy, pdfOutline]);

  React.useEffect(() => {
    return () => {
      if (outlineSyncPauseTimerRef.current) {
        clearTimeout(outlineSyncPauseTimerRef.current);
      }
    };
  }, []);

  const activeOutlineEntry = useMemo(() => {
    if (outlineSyncPaused) {
      return undefined;
    }
    return findActiveOutlineKey(outlineIndex, debouncedCurPage);
  }, [outlineSyncPaused, outlineIndex, debouncedCurPage]);

  React.useEffect(() => {
    if (outlineSyncPaused || outlineSyncPausedRef.current) {
      // 暂停期间保留用户点击设置的高亮，避免导航过程中的滚动同步抢走高亮
      return;
    }
    if (isProgrammaticScroll()) {
      // 本次 curPage 变化由程序滚动（outline 导航/翻页/恢复位置）引起，
      // 不触发滚动同步，避免定位过程把高亮改到其他章节。
      return;
    }
    const lock = outlineLockRef.current;
    if (lock) {
      // 定位校准：点击后的定位滚动已到达（curPage 已更新），若实际到达页
      // 与解析目标页偏差在 1 页内（行高/渲染残留偏差导致），将 lock.page
      // 校准为实际到达页，保持用户点击章节的高亮，避免定位偏差被滚动同步
      // 接管后把高亮切到其他章节。仅在定位后短时间内校准一次，
      // 之后用户手动滚动不受影响。
      if (
        !lock.calibrated &&
        Date.now() - lock.createdAt < 2000 &&
        curPage > 0 &&
        Math.abs(curPage - lock.page) <= 1
      ) {
        lock.page = curPage;
        lock.calibrated = true;
      }
      // 点击后定位期间：只要当前浏览位置仍落在 lock 所在页
      // （curPage / debouncedCurPage / 滚动同步计算结果任一命中），
      // 就保持用户点击章节的高亮，防止同页多个章节或定位偏差
      // 时滚动同步把高亮换到其他章节。只有用户手动滚动离开
      // lock 所在页后，才放行给滚动同步。
      const stillOnLockPage =
        curPage === lock.page ||
        debouncedCurPage === lock.page ||
        (activeOutlineEntry != null && activeOutlineEntry.page === lock.page);
      if (stillOnLockPage) {
        setAndDispatchActiveOutline({
          key: lock.key,
          ancestorKeys: lock.ancestorKeys,
        });
        return;
      }
      outlineLockRef.current = null;
    }
    if (activeOutlineEntry) {
      // 用户手动滚动/翻页：高亮跟随当前页对应的 outline 章节
      setAndDispatchActiveOutline({
        key: activeOutlineEntry.key,
        ancestorKeys: activeOutlineEntry.ancestorKeys,
      });
    } else {
      setAndDispatchActiveOutline({ ancestorKeys: [] });
    }
  }, [activeOutlineEntry, outlineSyncPaused, debouncedCurPage, curPage]);

  const pauseOutlineScrollSync = useCallback(() => {
    outlineSyncPausedRef.current = true;
    setOutlineSyncPaused(true);
    if (outlineSyncPauseTimerRef.current) {
      clearTimeout(outlineSyncPauseTimerRef.current);
    }
    outlineSyncPauseTimerRef.current = setTimeout(() => {
      outlineSyncPausedRef.current = false;
      setOutlineSyncPaused(false);
      outlineSyncPauseTimerRef.current = null;
    }, 800);
  }, []);
  const handleOutlineNavigation = useCallback(
    async (req: PdfOutlineNavRequest) => {
      if (!pdfProxy) {
        console.warn("PDF not loaded yet, cannot navigate outline");
        return;
      }
      const pageNum = await resolveOutlinePageNumber(pdfProxy, req.dest);
      if (pageNum && pageNum > 0) {
        scrollToPage(pageNum, virtualListRef, "start");
        if (req.key) {
          // 先暂停滚动同步并写入锁，确保点击后第一次定位期间
          // 高亮保持用户点击的章节，不被定位滚动触发的高亮更新覆盖。
          pauseOutlineScrollSync();
          outlineLockRef.current = {
            key: req.key,
            ancestorKeys: req.ancestorKeys ?? [],
            page: pageNum,
            createdAt: Date.now(),
            calibrated: false,
          };
        }
      } else {
        console.warn("Unable to resolve outline destination to page", req.dest);
      }
      const destPos = await resolveOutlineDestPosition(pdfProxy, req.dest);
      if (destPos && projInfo && projInfo.main_file) {
        getSrcPosition({
          project_id: props.projectId,
          main_file: projInfo.main_file.name,
          page: destPos.page,
          h: destPos.h,
          v: destPos.v,
        });
      }
    },
    [
      pdfProxy,
      pauseOutlineScrollSync,
      virtualListRef,
      projInfo,
      props.projectId,
    ],
  );

  const handlePdfPageClick = useCallback(
    (page: number, h: number, v: number) => {
      if (!projInfo || !projInfo.main_file) {
        return;
      }
      getSrcPosition({
        project_id: props.projectId,
        main_file: projInfo.main_file.name,
        page,
        h,
        v,
      });
    },
    [projInfo, props.projectId],
  );

  React.useEffect(() => {
    if (!outlineNavRequest || outlineNavRequest.id === outlineNavHandledIdRef.current) {
      return;
    }
    outlineNavHandledIdRef.current = outlineNavRequest.id;
    void handleOutlineNavigation(outlineNavRequest);
  }, [outlineNavRequest, handleOutlineNavigation]);

  React.useEffect(() => {
    let devModelFlag = localStorage.getItem("devModel");
    if (devModelFlag && Boolean(devModelFlag) === true) {
      setDevModel(true);
    } else {
      setDevModel(false);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleVisibilityChange = () => {
    let showPreview = localStorage.getItem(
      props.projectId + ":needShowPreviewTab",
    );
    if (
      showPreview &&
      showPreview === "true" &&
      document.visibilityState === "visible"
    ) {
      showPreviewTab("pdfview");
      localStorage.removeItem(props.projectId + ":needShowPreviewTab");
    }
  };

  React.useEffect(() => {
    setTexCompileResult(compileResultType);
  }, [compileResultType]);

  React.useEffect(() => {
    getLatestCompile(props.projectId);
  }, [props.projectId]);

  React.useEffect(() => {
    if (projInfo && Object.keys(projInfo).length > 0) {
      setCurProjInfo(projInfo);
    }
  }, [projInfo]);

  React.useEffect(() => {
    if (latestComp && Object.keys(latestComp).length > 0) {
      if (latestComp.path && latestComp.path.length > 0) {
        const pdfVersion = extractPdfVersion(latestComp.path);
        let newPdfUrl = "/tex/file/pdf/partial?proj_id=" + props.projectId;
        if (pdfVersion) {
          newPdfUrl += "&v=" + pdfVersion;
        }
        updatePdfUrl(newPdfUrl);
      } else {
        compile(props.projectId.toString());
      }
    }
  }, [latestComp]);

  React.useEffect(() => {
    if (!remoteCompileResult || Object.keys(remoteCompileResult).length === 0) {
      return;
    }
    let proj_id = remoteCompileResult.project_id;
    let vid = remoteCompileResult.out_path;
    if (proj_id && vid) {
      let newPdfUrl = "/tex/file/pdf/partial?proj_id=" + props.projectId;
      updatePdfUrl(newPdfUrl);
    }
  }, [remoteCompileResult]);

  React.useEffect(() => {
    if (endSignal && endSignal.length > 0) {
      let result = JSON.parse(endSignal);
      setLatestCompile(result.comp);
      setCompileQueue(result.queue);
      const backendResult = compileResultFromBackend(result?.queue?.comp_result);
      if (backendResult !== null && backendResult !== CompileResultType.PROCESSING) {
        setContextCompileResultType(backendResult);
      }
      if (
        result &&
        result.queue &&
        result.queue.comp_result === CompileResultType.SUCCESS
      ) {
        if (document.visibilityState === "visible") {
          showPreviewTab("pdfview");
        } else {
          localStorage.setItem(
            result.comp.project_id + ":needShowPreviewTab",
            "true",
          );
        }
      }
    }
  }, [endSignal]);

  React.useEffect(() => {
    if (texPdfUrl && texPdfUrl.length > 0) {
      setCurPdfUrl(texPdfUrl);
    }
  }, [texPdfUrl]);

  React.useEffect(() => {
    if (tabName && tabName.length > 0) {
      setCurPreviewTab(tabName);
    }
  }, [tabName]);

  const renderPreviewTab = () => {
    const isPdf = curPreviewTab === "pdfview";
    const isLog = curPreviewTab === "logview";
    return (
      <>
        <div
          className={styles.previewTabPane}
          style={{ display: isPdf ? "flex" : "none" }}
        >
          {renderPdfView()}
        </div>
        <div
          className={styles.previewTabPane}
          style={{ display: isLog ? "flex" : "none" }}
        >
          {renderLogView()}
        </div>
      </>
    );
  };

  const renderLogView = () => {
    return <PreviewerLogPanel />;
  };

  const setPageNum = useCallback((pageNum: number) => {
    setNumPages(pageNum);
  }, []);

  const handleOutlineLoaded = useCallback((outline: OutlineItemRaw[]) => {
    setAndDispatchPdfOutline(outline);
  }, []);

  // https://stackoverflow.com/questions/76834748/react-pdf-gives-typeerror-cannot-read-properties-of-null-reading-sendwithprom
  const opt = useMemo(() => {
    return {
      ...getPdfjsOptions(),
      httpHeaders: buildPdfHttpHeaders(),
    };
  }, [curPdfUrl]);

  const renderPdfPreview = () => (
    <MemoizedPDFPreview
      ref={pdfPreviewRef}
      curPdfUrl={curPdfUrl}
      projId={props.projectId}
      viewModel={props.viewModel}
      setPageNum={setPageNum}
      virtualListRef={virtualListRef}
      pdfOptions={opt}
      curPdfPage={props.curPage}
      onOutlineLoaded={handleOutlineLoaded}
      onPdfLoaded={setPdfProxy}
      onPageClick={handlePdfPageClick}
    />
  );

  const renderPdfView = () => {
    if (!curPdfUrl || !props.projectId) {
      return <div>{t("tips_loading")}</div>;
    }
    if (props.viewModel === "fullscreen") {
      return (
        <div
          id="pdf-fullscreen-preview-container"
          className={styles.fullscreenPreviewLayout}
        >
          <Split
            visible={[2, 3]}
            style={{
              width: "100%",
              height: "100%",
              minHeight: 0,
              border: "0px solid #d5d5d5",
              borderRadius: 3,
            }}
          >
            <div id="pdf-outline" className={styles.fullscreenOutlinePane}>
              <PdfOutlinePanel variant="fullscreen" />
            </div>
            <div
              id="fc-preview-container"
              className={styles.fcPdfPreviewContainer}
            >
              {renderPdfPreview()}
            </div>
          </Split>
        </div>
      );
    }
    return (
      <div id="pdf-preview-container" className={styles.pdfPreviewContainer}>
        {renderPdfPreview()}
      </div>
    );
  };

  const renderPreviewHeaderAction = () => {
    if (curPreviewTab === "pdfview") {
      return (
        <div className={styles.rightAction}>
          {!devModel ? (
            <div></div>
          ) : (
            <button
              className={styles.previewIconButton}
              data-bs-toggle="tooltip"
              title={t("btn_debug_app")}
              onClick={() => {
                getPreviewUrl(props.projectId).then((res: any) => {
                  if (ResponseHandler.responseSuccess(res)) {
                    let url =
                      "/preview/fullscreen/independent?projId=" +
                      props.projectId +
                      "&curPage=" +
                      curPage +
                      "&pdfUrl=" +
                      encodeURIComponent(res.result);
                    window.open(url, "_blank", "noopener,noreferrer");
                  }
                });
              }}
            >
              <i className="fa-solid fa-check"></i>
            </button>
          )}
          {!devModel ? (
            <div></div>
          ) : (
            <button
              className={styles.previewIconButton}
              data-bs-toggle="tooltip"
              title={t("btn_debug_app")}
              onClick={() => {
                debugApp(virtualListRef, props.projectId);
              }}
            >
              <i className="fa-solid fa-check"></i>
            </button>
          )}
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_restore_scroll")}
            onClick={() => {
              if (virtualListRef && virtualListRef.current) {
                restorePdfOffset(
                  props.projectId,
                  props.viewModel,
                  virtualListRef,
                );
              }
            }}
          >
            <i className="fa-solid fa-hashtag"></i>
          </button>
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_open_in_broswer")}
            onClick={() => {
              handleOpenInBrowserDirect(props.projectId);
            }}
          >
            <i className="fa-brands fa-chrome"></i>
          </button>
          {props.viewModel === "fullscreen" ? (
            <button
              className={styles.previewIconButton}
              data-bs-toggle="tooltip"
              title={t("btn_maximize")}
              onClick={() => {
                enterFullScreen();
              }}
            >
              <i className="fa-solid fa-expand"></i>
            </button>
          ) : null}
          {props.viewModel === "fullscreen" ? (
            <button
              className={styles.previewIconButton}
              data-bs-toggle="tooltip"
              title={t("btn_home")}
              onClick={() => {
                window.location.href = "/";
              }}
            >
              <i className="fa-solid fa-home"></i>
            </button>
          ) : null}
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_scroll_top")}
            onClick={() => {
              handleScrollTop(virtualListRef, props.projectId);
            }}
          >
            <i className="fa-solid fa-arrow-up"></i>
          </button>
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_nav_src")}
            onClick={() => {
              if (!BaseMethods.isNull(curProjInfo)) {
                handleSrcLocate(
                  props.projectId,
                  curProjInfo!,
                  t("msg_empty_proj_info"),
                );
              }
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_download_pdf")}
            onClick={() => {
              handleDownloadPdf(
                curPdfUrl,
                curProjInfo?.main.proj_name as string,
                props.projectId,
              );
            }}
          >
            <i className="fa-solid fa-download"></i>
          </button>
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_zoomin")}
            id="zoominbutton"
            onClick={() => {
              handleZoomIn();
            }}
          >
            <i className="fa fa-search-plus"></i>
          </button>
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_zoomout")}
            id="zoomoutbutton"
            onClick={() => {
              handleZoomOut();
            }}
          >
            <i className="fa fa-search-minus"></i>
          </button>
          <button
            className={styles.previewIconButton}
            data-bs-toggle="tooltip"
            title={t("btn_fullscreen")}
            id="fullscreenbutton"
            onClick={() => {
              handleFullScreen(curPage);
            }}
          >
            <i className="fa fa-maximize"></i>
          </button>
        </div>
      );
    }
    if (curPreviewTab === "logview") {
      return <div></div>;
    }
    return <div></div>;
  };

  const renderCompiled = (texCompileResult: CompileResultType) => {
    if (texQueue && Object.keys(texQueue).length > 0) {
      if (texCompileResult === CompileResultType.FAILED) {
        return <i className="fa-solid fa-bug text-danger"></i>;
      } else if (texCompileResult === CompileResultType.SUCCESS) {
        return <i className="fa-solid fa-square-check text-success"></i>;
      } else if (texCompileResult === CompileResultType.PROCESSING) {
        return <i className="fa-solid fa-spinner"></i>;
      }
    }
  };

  const handleNavPageChange = (e: any) => {
    let val = e.target.value;
    setAndDispatchPdfPage(val, props.projectId, "handleNavPageChange");
  };

  const renderPageNaviation = () => {
    return (
      <div className={styles.previewPageNav}>
        <input
          value={curPage || undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleNavPageChange(e);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              const navPage = (event.target as HTMLInputElement).value;
              const tweakedPageNum = Number(navPage);
              if (tweakedPageNum >= 0 && tweakedPageNum < 10000) {
                scrollToPage(tweakedPageNum, virtualListRef);
              }
            }
          }}
        ></input>
        <div>/</div>
        <div>{numPages || 0}</div>
      </div>
    );
  };

  const renderLeftTab = () => {
    if (props.viewModel === "fullscreen") {
      return (
        <div className={styles.leftAction}>
          <button
            className={styles.previewButton}
            onClick={() => {
              setCurPreviewTab("pdfview");
            }}
          >
            <i className="fa-regular fa-file-pdf"></i> {t("tab_preview")}
          </button>
        </div>
      );
    }
    return (
      <div className={styles.leftAction}>
        <button
          className={styles.previewButton}
          onClick={() => {
            setCurPreviewTab("pdfview");
          }}
        >
          <i className="fa-regular fa-file-pdf"></i> {t("tab_preview")}
        </button>
        <button
          className={styles.previewButton}
          onClick={() => {
            setCurPreviewTab("logview");
          }}
        >
          <i className="fa-regular fa-file-lines"></i> {t("tab_log")}{" "}
          {renderCompiled(texCompileResult)}
        </button>
      </div>
    );
  };

  return (
    <div
      id="preview"
      className={
        props.viewModel === "fullscreen" ? styles.fspreview : styles.preview
      }
    >
      <div id="previewHeader" className={styles.previewHader}>
        {renderLeftTab()}
        {renderPageNaviation()}
        {renderPreviewHeaderAction()}
      </div>
      {renderPreviewTab()}
      <ToastContainer></ToastContainer>
    </div>
  );
};

export default Previewer;
